import { useDispatch } from 'react-redux';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { Modal, MODAL_SIZES, MODAL_VARIANTS, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { NODE_HAS_UNSAVED_CHANGES_MODAL } from '../../../constants';
import { closeModal } from '../../../ducks';
import { NodeData } from '../../../types';

export const KEEP_EDITS_BUTTON_TEST_ID = 'keep-node-changes-button';
export const CLEAR_EDITS_BUTTON_TEST_ID = 'clear-node-changes-button';

export interface NodeHasUnsavedChangesModalProps {
  /** Callback to close node editor */
  onCloseEditor: () => void;
  /** callback to update node */
  onUpdateEditor: () => void;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Whether node editor can be updated */
  canUpdate?: boolean;
  /** Is send node? */
  isSendNodeEditor?: boolean;
  /** The tile's saved node data */
  nodeData?: Partial<NodeData>;
}

export const NodeHasUnsavedChangesModal: React.FC<
  React.PropsWithChildren<NodeHasUnsavedChangesModalProps>
> = ({
  onCloseEditor,
  onUpdateEditor,
  isOpen,
  canUpdate = true,
  nodeData = {},
  isSendNodeEditor,
}) => {
  const dispatch = useDispatch();
  const { campaignId } = nodeData;
  const isNewCampaign = isSendNodeEditor && !campaignId;

  const handleCloseModal = () => dispatch(closeModal(NODE_HAS_UNSAVED_CHANGES_MODAL));

  const handleDiscard = () => {
    onCloseEditor();
    handleCloseModal();
  };

  const handleUpdate = () => {
    onUpdateEditor();
    handleCloseModal();
  };

  return (
    <Modal
      id={NODE_HAS_UNSAVED_CHANGES_MODAL}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.SMALL}
      variant={MODAL_VARIANTS.WARNING}
    >
      <ModalHeader>Unsaved changes</ModalHeader>
      <ModalBody>
        <p>
          This tile has been edited. Do you want to save your changes
          {isNewCampaign ? ' and create the campaign' : ''}?
        </p>
      </ModalBody>
      <ModalFooter>
        <Button
          data-test={CLEAR_EDITS_BUTTON_TEST_ID}
          onClick={handleDiscard}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.SECONDARY}
        >
          Discard changes
        </Button>
        {
          <Button
            data-test={KEEP_EDITS_BUTTON_TEST_ID}
            autoFocus
            onClick={handleUpdate}
            size={BUTTON_SIZES.LARGE}
            variant={BUTTON_VARIANTS.PRIMARY}
            isDisabled={!canUpdate}
          >
            {isNewCampaign ? 'Create campaign' : 'Save tile'}
          </Button>
        }
      </ModalFooter>
    </Modal>
  );
};
